import { api } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { demoAdmin } from '@/features/admin/demo/demoAdmin'
import type { AdminScope, NodeRef, PendingInvitation } from '@/features/admin/types'
import type { AcceptedInvitation, InvitationPreview, RoleOption } from '@/features/auth/types'
import { demoInvitations } from '@/features/invitations/demo/demoInvitations'
import type { InviteInput } from '@/features/invitations/schemas/invitationSchemas'
import type { InvitationCreated } from '@/features/invitations/types'
import { env } from '@/lib/env'

/**
 * Invitations replace self-registration. A branch manager or company admin
 * invites someone into a named location; the invitee opens the link, sets a
 * password, then signs in like everyone else. The backend sends no e-mail — the
 * link travels by hand.
 */
/** Exactly the node the role's tier needs: a franchise or a Standort, never from the session. */
export interface InviteNode {
  franchise?: string
  location?: string
}

/** The request names its node; the backend never takes it from the session. */
export function toInviteRequest(input: InviteInput, node: InviteNode | string) {
  const target = typeof node === 'string' ? { location: node } : node
  return { email: input.email, fullName: input.fullName, role: input.role, ...target }
}

/** `RoleOption` plus `tier`, added by the admin contract (spec §5.4); absent means a Standort role. */
export function toRoleOption(option: RoleOption): RoleOption {
  return { key: option.key, label: option.label, tier: option.tier ?? 'location' }
}

/** One live invitation (spec §5.4, new endpoint). The token is never part of it. */
interface InvitationRowResponse {
  publicId: string
  email: string
  fullName: string
  role: { key: string; label: string }
  node: NodeRef
  invitedBy: { publicId: string; displayName: string } | string | null
  createdAt: string
  expiresAt: string
}

export function toPendingInvitation(row: InvitationRowResponse): PendingInvitation {
  return {
    publicId: row.publicId,
    email: row.email,
    fullName: row.fullName,
    role: { ...row.role },
    node: { ...row.node },
    invitedBy:
      row.invitedBy === null || typeof row.invitedBy === 'string'
        ? row.invitedBy
        : row.invitedBy.displayName,
    createdAt: row.createdAt,
    expiresAt: row.expiresAt,
  }
}

export const invitationsApi = {
  /** Only the roles a manager may hand out; needs `USER_INVITE`. */
  fetchRoles: () =>
    env.demoMode
      ? demoInvitations.fetchRoles()
      : api
          .get<RoleOption[]>(endpoints.invitations.roles)
          .then((options) => options.map(toRoleOption)),

  create: (input: InviteInput, locationPublicId: string) =>
    invitationsApi.createAt(input, { location: locationPublicId }),

  /** An invitation at a franchise (a partner) or a Standort (staff, manager). */
  createAt: (input: InviteInput, node: InviteNode): Promise<InvitationCreated> =>
    env.demoMode
      ? demoAdmin.createInvitation(input, node)
      : api.post<InvitationCreated>(endpoints.invitations.create, toInviteRequest(input, node)),

  /** Live invitations only — not accepted, expired or revoked. */
  list: async (scope: AdminScope): Promise<PendingInvitation[]> => {
    if (env.demoMode) return demoAdmin.listInvitations(scope)
    const response = await api.get<{ items: InvitationRowResponse[] }>(endpoints.invitations.list, {
      params: scope,
    })
    return response.items.map(toPendingInvitation)
  },

  /** A soft revoke; the link stops working at once. */
  revoke: async (invitationId: string): Promise<void> => {
    if (env.demoMode) return demoAdmin.revokeInvitation(invitationId)
    await api.delete<null>(endpoints.invitations.revoke(invitationId))
  },

  /** Public. Any unusable token — expired, used, replaced, unknown — is a 404. */
  preview: (token: string) =>
    env.demoMode
      ? demoInvitations.preview(token)
      : api.get<InvitationPreview>(endpoints.invitations.preview(token)),

  /** Public and CSRF-exempt. Creates the account but starts no session. */
  accept: (token: string, password: string) =>
    env.demoMode
      ? demoInvitations.accept(token)
      : api.post<AcceptedInvitation>(endpoints.invitations.accept(token), { password }),
}
